package nl.volleyanalyzer.web.controller

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.merge
import kotlinx.coroutines.flow.stateIn
import kotlinx.datetime.format
import kotlinx.html.*
import nl.volleyplanner.api.sports.CompetitionType
import nl.volleyplanner.api.sports.Match
import nl.volleyplanner.web.DATE_TEXT_NO_YEAR_SHORT_DAY_MONTH_FORMAT
import nl.volleyplanner.web.base.HTMLComponent
import nl.volleyplanner.web.base.icon
import nl.volleyplanner.web.base.simpleComponent
import nl.volleyplanner.web.base.spinner
import org.w3c.dom.HTMLElement

class MatchPreparationAnalyzerComponent(
    private val pouleMatchesData: PouleMatchesData,
    private val matchToAnalyze: Match
) : HTMLComponent() {
    private val allPoulesByTeam = pouleMatchesData.pouleMatchesResponse.teams.values
        .map { Pair(it.name, it.poules) }
        .toMap()

    private val allPouleFlowsByPouleId = allPoulesByTeam
        .map { it.value }
        .flatten().distinct()
        .associateWith { PouleMatchesDataManager.subscribeToPoule(it.pouleId) }

    fun getMatches(teamName: String) : List<Match>? {
        if (allPoulesByTeam.getValue(teamName)
            .any { allPouleFlowsByPouleId.getValue(it).value == null }
            ) return null

        val allPouleMatches = allPoulesByTeam.getValue(teamName).map { allPouleFlowsByPouleId.getValue(it).value!! }

        return allPouleMatches
            .map { pouleData -> pouleData.pouleMatchesResponse.matches.filter { it.teamHome == teamName || it.teamGuest == teamName } }
            .flatten()
    }

    fun FlowContent.renderVorm(teamName: String) {
        div (classes = "border-secondary") {
            h6 { +"Vorm" }

            val teamMatches = getMatches(teamName)
            if (teamMatches == null) {
                spinner()
            } else {

                teamMatches
                    .filter { it.hasScore() }
                    .sortedByDescending { it.startDateTime }
                    .forEach { match ->
                        val teamIdentifier = match.identifyTeam(teamName)
                        val opposingTeamIdentifier = !teamIdentifier
                        val teamSets = match.getSets(teamIdentifier)
                        val opposingSets = match.getSets(opposingTeamIdentifier)
                        val result = when {
                            teamSets == null || opposingSets == null -> "no_result"
                            teamSets > opposingSets -> "win"
                            teamSets < opposingSets -> "lose"
                            teamSets == opposingSets -> "tie"
                            else -> "no_result"     // Impossible scenario
                        }
                        val bgColor = when (result) {
                            "win" -> "bg-success"
                            "lose" -> "bg-danger"
                            "tie" -> "bg-warning"
                            else -> "bg-secondary"
                        }

                        div(classes = "border-top border-secondary row py-1") {
                            classes += "${bgColor}-subtle"
                            div(classes = "col-12 row align-items-center") {
                                div(classes = "col-3 px-0 text-center") {
                                    +(match.startDateTime?.date?.format(DATE_TEXT_NO_YEAR_SHORT_DAY_MONTH_FORMAT)
                                        ?: "ntb")
                                }
                                div(classes = "col-9 pe-2") {
                                    val oppossingTeamName = match.getTeamName(opposingTeamIdentifier)
                                    val lastRoundPosition = pouleMatchesData.standings?.roundStandings?.values?.last()?.records?.keys?.indexOf(oppossingTeamName)

                                    +"$oppossingTeamName${lastRoundPosition?.let { " (${it + 1})" } ?: ""}"
                                    span(classes = "small") {
                                        +"(${if (teamIdentifier.isHome()) "thuis" else "uit"})"
                                    }
                                }
                            }
                            div(classes = "col-12 row mt-1 align-items-center") {
                                div(classes = "col-3 px-0 text-center") {
                                    when(match.competitionType) {
                                        CompetitionType.Season -> icon("bi-calendar-event")
                                        CompetitionType.CupTournament -> icon("bi-trophy")
                                        CompetitionType.Recreative -> icon("bi-calendar-event")
                                        CompetitionType.SittingVolleyball -> icon("bi-calendar-event")
                                        CompetitionType.Championships -> icon("bi-trophy")
                                    }
                                    matchSetResultBadge(match, teamIdentifier)
                                }
                                div(classes = "col-9 pe-2") {
                                    setResultsBadges(match, teamIdentifier)
                                }
                            }
                            // Get match win, lose or tie
                            // Display set results
                            // Display set points
                        }
                    }

                div(classes = "row mt-5 border border-bg-secondary-subtle rounded-3 p-2") {
                    div {
                        icon("bi-trophy")
                        +"Beker"
                    }
                    div {
                        icon("bi-calendar-event")
                        +"Competitie"
                    }
                }
            }
        }
    }

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        return consumer.div {
            div (classes = "row") {
                div(classes = "col d-flex justify-content-center align-items-center") {
                    div (classes = "rounded-1 border border-secondary me-2") {
                        style = "background-color: rgb(234, 134, 143); width: 0.8em; height: 0.8em;"
                    }
                    h2 { +"${matchToAnalyze.teamHome} vs ${matchToAnalyze.teamGuest}" }
                    div (classes = "rounded-1 border border-secondary ms-2") {
                        style = "background-color: rgb(110, 168, 254); width: 0.8em; height: 0.8em;"
                    }
                }
            }
            div (classes = "row") {
                div(classes = "col-3 border-top border-secondary") {
                    h5 (classes = "text-center mt-2") { +matchToAnalyze.teamHome }

                    allPoulesByTeam.getValue(matchToAnalyze.teamHome)
                        .filter { it.pouleId != pouleMatchesData.pouleMatchesResponse.fullPoule.pouleId }
                        .map { allPouleFlowsByPouleId.getValue(it) }
                        .forEach { additionalPouleFlow ->
                            div (classes = "row") {
                                if (additionalPouleFlow.value == null) {
                                    spinner()
                                } else {
                                    val poule = additionalPouleFlow.value!!.pouleMatchesResponse.fullPoule
                                    val standings = additionalPouleFlow.value!!.standings
                                    if (standings == null) {
                                        h6 { +"Ook in ${poule.name}" }
                                    } else {
                                        simpleComponent(
                                            PouleStandingsChartComponent(
                                                standings,
                                                ChartOptions(
                                                    displayLegend = false,
                                                    displayYAxis = false,
                                                    enableMethodSelection = false
                                                ),
                                                coloredTeams = listOf(matchToAnalyze.teamHome)
                                            )
                                        )
                                        val lastRound = standings.roundStandings.values.last()
                                        p (classes="fw-semibold") {
                                            +"${lastRound.records.keys.indexOf(matchToAnalyze.teamHome) + 1}e in ${poule.name}"
                                        }
                                    }
                                }
                            }
                        }
                }
                div(classes = "col-6 border border-secondary border-top-0") {
                    div {
                        val standings = pouleMatchesData.standings
                        if (standings != null) {
                            simpleComponent(
                                PouleStandingsChartComponent(
                                    standings,
                                    ChartOptions(
                                        displayLegend = false,
                                        displayYAxis = true,
                                        enableMethodSelection = false
                                    ),
                                    coloredTeams = listOf(matchToAnalyze.teamHome, matchToAnalyze.teamGuest)
                                )
                            )
                        }
                    }
                }
                div(classes = "col-3 border-top border-secondary") {
                    h5 (classes = "text-center mt-2") { +matchToAnalyze.teamGuest }

                    allPoulesByTeam.getValue(matchToAnalyze.teamGuest)
                        .filter { it.pouleId != pouleMatchesData.pouleMatchesResponse.fullPoule.pouleId }
                        .map { allPouleFlowsByPouleId.getValue(it) }
                        .forEach { additionalPouleFlow ->
                            div (classes = "row") {
                                if (additionalPouleFlow.value == null) {
                                    spinner()
                                } else {
                                    val poule = additionalPouleFlow.value!!.pouleMatchesResponse.fullPoule
                                    val standings = additionalPouleFlow.value!!.standings
                                    if (standings == null) {
                                        h6 { +"Ook in ${poule.name}" }
                                    } else {
                                        simpleComponent(
                                            PouleStandingsChartComponent(
                                                standings,
                                                ChartOptions(
                                                    displayLegend = false,
                                                    displayYAxis = false,
                                                    enableMethodSelection = false
                                                ),
                                                coloredTeams = listOf("N/A", matchToAnalyze.teamGuest)
                                            )
                                        )
                                        val lastRound = standings.roundStandings.values.last()
                                        h6 { +"${lastRound.records.keys.indexOf(matchToAnalyze.teamGuest) + 1}e in ${poule.name}" }
                                    }
                                }
                            }
                        }
                }
            }
            div (classes = "row") {
                div(classes = "col-5 border-end border-secondary pt-2") {
                    renderVorm(matchToAnalyze.teamHome)
                }
                div(classes = "col-2") { }
                div(classes = "col-5 border-start border-secondary pt-2") {
                    renderVorm(matchToAnalyze.teamGuest)
                }
            }
        }
    }

    override suspend fun getState() = allPouleFlowsByPouleId.values.merge().stateIn(CoroutineScope(Dispatchers.Default))
}